import { T, useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useMemo, useRef, useState } from 'react';

import { trackEvent } from '@/modules/analytics';
import { highestRecommendedProgramUuid, orderProgramsByScore } from '@/modules/api';
import { useRulesEngine } from '@/modules/rulesEngine';
import { CtaButton, Screen, type ScreenHandle, ScreenHeading } from '@/modules/ui';

import { ProgramListSkeleton } from './components/ProgramListSkeleton';
import { ProgramSelectableRow } from './components/ProgramSelectableRow';

/**
 * SELECT PROGRAM — the full-choice list (Sparta's ProgramMatchingContainer /
 * stylos's ProgramsListPage). Reached for the soft-recommend flow. The list is
 * ordered by recommendation score (highest first), with the top-scored program
 * surfaced into its own "Recommended for you" section. Selecting + Continue
 * hands off to the rules engine's `selectProgram`, which routes to
 * coaching-method or review based on the program's coaching metadata.
 */
export function ProgramMatchingScreen() {
  const t = useT();
  const {
    prescribedPrograms,
    isLoadingPrograms,
    selectedProgramUuid,
    setSelectedProgram,
    selectProgram,
    user,
  } = useRulesEngine();

  const [showRequiredError, setShowRequiredError] = useState(false);
  const screenRef = useRef<ScreenHandle>(null);

  const { programs, recommended, others } = useMemo(() => {
    const ordered = orderProgramsByScore(prescribedPrograms);
    // The recommended section shows only when there's more than one program;
    // `orderProgramsByScore` sorts highest-score first, so it's `ordered[0]`.
    const recUuid =
      ordered.length > 1 ? highestRecommendedProgramUuid(prescribedPrograms) : undefined;
    const rec = recUuid ? ordered.find((p) => p.uuid === recUuid) : undefined;
    return {
      programs: ordered,
      recommended: rec,
      others: rec ? ordered.filter((p) => p.uuid !== rec.uuid) : ordered,
    };
  }, [prescribedPrograms]);

  const onSelect = (uuid: string) => {
    setSelectedProgram(uuid);
    setShowRequiredError(false);
    const program = programs.find((p) => p.uuid === uuid);
    trackEvent('program-matching-selected', {
      uuid: user?.uuid,
      programUrn: program?.urn,
      programTitle: program?.title,
      isRecommended: uuid === recommended?.uuid,
    });
  };

  const onContinue = () => {
    if (!selectedProgramUuid) {
      setShowRequiredError(true);
      // Surface the (top-of-body) validation message even if the user is
      // scrolled down the list.
      screenRef.current?.scrollToTop();
      return;
    }
    selectProgram(selectedProgramUuid);
  };

  return (
    <Screen
      ref={screenRef}
      footer={
        <CtaButton
          isDisabled={isLoadingPrograms}
          onPress={onContinue}
          testID='program-matching-continue'
        >
          <T _str='Continue' />
        </CtaButton>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading title={t('Choose the program that best aligns with your health goals')} />

        {showRequiredError ? (
          <Text size='sm' className='text-error-700'>
            <T _str='Please select a program to continue.' />
          </Text>
        ) : null}

        {isLoadingPrograms ? (
          <ProgramListSkeleton />
        ) : programs.length === 0 ? (
          <Text size='md' className='text-typography-700'>
            <T _str='We couldn’t load programs right now. Please refresh and try again.' />
          </Text>
        ) : (
          <VStack space='4xl'>
            {recommended ? (
              <VStack space='lg'>
                <Text size='md' weight='semibold' className='text-typography-900'>
                  <T _str='Recommended for you' />
                </Text>
                <ProgramSelectableRow
                  program={recommended}
                  isSelected={selectedProgramUuid === recommended.uuid}
                  onSelect={onSelect}
                />
              </VStack>
            ) : null}

            {others.length > 0 ? (
              <VStack space='lg'>
                {recommended ? (
                  <Text size='md' weight='semibold' className='text-typography-900'>
                    <T _str='Other options' />
                  </Text>
                ) : null}
                {others.map((program) => (
                  <ProgramSelectableRow
                    key={program.uuid}
                    program={program}
                    isSelected={selectedProgramUuid === program.uuid}
                    onSelect={onSelect}
                  />
                ))}
              </VStack>
            ) : null}
          </VStack>
        )}
      </VStack>
    </Screen>
  );
}
